<template>
    <el-card>
        <div slot="header" class="move">
            <h3>Broker Income Leaderboards</h3>
        </div>
        <horizontal-bar-chart :data="datasets" :options="options" class="col-12"
                              :style="{'height': ((totalBrokers * 40) + 70) + 'px'}"></horizontal-bar-chart>
    </el-card>
</template>

<script>
import HorizontalBarChart from '../../ambition/dashboard/charts/HorizontalBarChart'

export default {
    name: 'BrokerSettledChart',
    components: {
        HorizontalBarChart
    },
    props: [
        'brokerSettled',
        'appearance',
        'tenantId',
        'dashboardId'
    ],
    data() {
        return {
            brokerSettlements: [],
            datasets: [],
            labels: [],
            options: {
                responsive: true,
                maintainAspectRatio: false,
                hover: {
                    animationDuration: 1
                },
                layout: {
                    padding: {
                        left: 0,
                        right: 0,
                        top: 0,
                        bottom: 0
                    }
                },
                scales: {
                    xAxes: [{
                        stacked: true,
                        barPercentage: 1,
                        categoryPercentage: 0.8,
                        ticks: {
                            precision: 0,
                            fontFamily: "Poppins",
                            padding: 5,
                            boxWidth: 15,
                            fontSize: 12,
                            fontColor: "#000",
                            fontStyle: "normal",
                            callback: function (value, index, values) {
                                return this.commarize(String(Math.round(value)));
                            }.bind(this)
                        }
                    }],
                    yAxes: [{
                        stacked: true,
                        barPercentage: 1,
                        categoryPercentage: 0.8,
                        ticks: {
                            fontFamily: "Poppins",
                            padding: 5,
                            boxWidth: 15,
                            fontSize: 12,
                            fontColor: "#000",
                            fontStyle: "normal",
                            callback: function (value, index, values) {
                                if (String(value).length > 15)
                                    return String(value).substring(0, 13) + '...'
                                else
                                    return value;
                            }
                        }
                    }]
                },
                legend: {
                    display: false,
                    position: 'bottom',
                    labels: {
                        fontFamily: "Poppins",
                        padding: 16,
                        boxWidth: 15,
                        fontSize: 12,
                        fontColor: "#000",
                        fontStyle: "normal"
                    }
                },
                tooltips: {
                    titleFontFamily: "Poppins",
                    titleFontSize: 12,
                    titleMarginBottom: 15,
                    bodyFontFamily: "Poppins",
                    bodyFontSize: 12,
                    bodySpacing: 15,
                    cornerRadius: 0,
                    intersect: true,
                    xPadding: 10,
                    yPadding: 10,
                    backgroundColor: this.graphBGColorChange(),
                    color: this.graphColorChange(),
                    callbacks: {
                        title: function (t, d) {
                            var idx = t[0].index;
                            return d.labels[idx];
                        },
                        label: function (t, d) {
                            var dstLabel = 'Income'; //d.datasets[t.datasetIndex].label.replace(/ .*/,'');
                            var xLabel = t.xLabel;
                            return ' ' + dstLabel + ': $' + Number(xLabel).toLocaleString("en-AU", { maximumFractionDigits: 2 });
                        }.bind(this)
                    },
                    enabled: false,
                    custom: function (tooltipModel) {
                        // Tooltip Element
                        var tooltipEl = document.getElementById('chartjs-tooltip');

                        // Create element on first render
                        if (!tooltipEl) {
                            tooltipEl = document.createElement('div');
                            tooltipEl.id = 'chartjs-tooltip';
                            tooltipEl.innerHTML = '<table></table>';
                            document.body.appendChild(tooltipEl);
                        }

                        // Hide if no tooltip
                        if (tooltipModel.opacity === 0) {
                            tooltipEl.style.opacity = 0;
                            return;
                        }

                        // Set caret Position
                        tooltipEl.classList.remove('above', 'below', 'no-transform');
                        if (tooltipModel.yAlign) {
                            tooltipEl.classList.add(tooltipModel.yAlign);
                        } else {
                            tooltipEl.classList.add('no-transform');
                        }

                        function getBody(bodyItem) {
                            return bodyItem.lines;
                        }

                        // Set Text
                        if (tooltipModel.body) {
                            var titleLines = tooltipModel.title || [];
                            var bodyLines = tooltipModel.body.map(getBody);

                            var innerHtml = '<thead>';

                            titleLines.forEach(function (title) {
                                innerHtml += '<tr><th>' + title + '</th></tr>';
                            });
                            innerHtml += '</thead><tbody>';

                            bodyLines.forEach(function (body, i) {
                                var colors = tooltipModel.labelColors[i];
                                var style = 'background:' + colors.backgroundColor;
                                style += '; border-color:' + colors.borderColor;
                                style += '; border-width: 2px';
                                var span = '<span style="' + style + '"></span>';
                                innerHtml += '<tr><td>' + span + body + '</td></tr>';
                            });
                            innerHtml += '</tbody>';

                            var tableRoot = tooltipEl.querySelector('table');
                            tableRoot.innerHTML = innerHtml;
                        }

                        // `this` will be the overall tooltip
                        var position = this._chart.canvas.getBoundingClientRect();

                        // Display, position, and set styles for font
                        tooltipEl.style.opacity = 1;
                        tooltipEl.style.position = 'absolute';
                        tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 10 + 'px';
                        tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY - 30 + 'px';
                        tooltipEl.style.backgroundColor = tooltipModel.backgroundColor;
                        tooltipEl.style.fontFamily = tooltipModel._bodyFontFamily;
                        tooltipEl.style.fontSize = tooltipModel.bodyFontSize + 'px';
                        tooltipEl.style.fontStyle = tooltipModel._bodyFontStyle;
                        tooltipEl.style.padding = tooltipModel.yPadding + 'px ' + tooltipModel.xPadding + 'px';
                        tooltipEl.style.pointerEvents = 'none';
                    }
                },
                animation: {
                    duration: 500,
                    easing: "easeOutQuart",
                    onComplete: function (chartElement) {
                        var ctx = chartElement.chart.ctx;
                        ctx.font = Chart.helpers.fontString(14, 'bold', 'Poppins');
                        ctx.textAlign = 'right';
                        ctx.textBaseline = 'bottom';

                        var brokerName = this.brokerSettlements.brokerName || '';

                        chartElement.chart.data.datasets.forEach(function (dataset) {
                            for (var i = 0; i < dataset.data.length; i++) {
                                var model = dataset._meta[Object.keys(dataset._meta)[0]].data[i]._model,
                                    left = dataset._meta[Object.keys(dataset._meta)[0]].data[i]._xScale.left,
                                    right = dataset._meta[Object.keys(dataset._meta)[0]].data[i]._model.x;
                                var dataIndex = dataset._meta[Object.keys(dataset._meta)[0]].data[i]._index;
                                var broker = Object.values(this.brokerSettlements.labelsIncome)[dataIndex];

                                if (broker == brokerName) {
                                    ctx.fillStyle = this.adjust('#fff', 0);
                                } else {
                                    ctx.fillStyle = this.adjust('#fff', 0);
                                }

                                var label = '$' + this.commarize(String(dataset.data[i]))

                                var text = ctx.measureText(label);

                                if (text.width < (right - left - 10)) {
                                    ctx.fillText(label, right - 5, model.y + 8, right - left);
                                }

                            }
                        }.bind(this));
                    }.bind(this)
                }
            }
        }
    },
    mounted() {
        Echo.private(`dashboard.${this.tenantId}.${this.dashboardId}`).listen(".dashboard-live", (event) => {
            this.brokerSettlements = event.brokerSettlement.settlements

            if (this.totalSettlements) {
                let labels = Object.values(this.brokerSettlements.labelsIncome);

                let values = [];

                values = this.brokerSettlements.values.total_income;

                this.datasets = {
                    datasets: [{
                        label: 'Total Settlements',
                        backgroundColor: function (chart) {

                            let brokerName = this.brokerSettlements.brokerName || '';
                            let broker = Object.values(this.brokerSettlements.labelsIncome)[chart.dataIndex];
                            let noOfBrokers = Object.values(this.brokerSettlements.labelsIncome).length;
                            let variance = Math.round(100 / noOfBrokers, 2);

                            if (broker == brokerName) {
                                return this.adjust('#D4AF37', 0);
                            } else {
                                return this.adjust('#005c99', chart.dataIndex * variance);
                            }

                        }.bind(this),
                        data: Object.values(values)
                    }],
                    labels: labels
                }
            }

            this.graphColorChange()
            this.graphBGColorChange()
        })
    },
    created() {
        Echo.private(`dashboard.${this.tenantId}.${this.dashboardId}`).listen(".dashboard-live", (event) => {
            this.brokerSettlements = event.brokerSettlement.settlements

            if (this.totalSettlements) {
                let labels = Object.values(this.brokerSettlements.labelsIncome);

                let values = [];

                values = this.brokerSettlements.values.total_income;

                this.datasets = {
                    datasets: [{
                        label: 'Total Settlements',
                        backgroundColor: function (chart) {
                            let brokerName = this.brokerSettlements.brokerName || '';
                            let broker = Object.values(this.brokerSettlements.labelsIncome)[chart.dataIndex];
                            let noOfBrokers = Object.values(this.brokerSettlements.labelsIncome).length;
                            let variance = Math.round(100 / noOfBrokers, 2);

                            if (broker == brokerName) {
                                return this.adjust('#D4AF37', 0);
                            } else {
                                return this.adjust('#005c99', chart.dataIndex * variance);
                            }

                        }.bind(this),
                        data: Object.values(values)
                    }],
                    labels: labels
                }
            }

            this.graphColorChange()
            this.graphBGColorChange()
        })
    },
    computed: {
        totalSettlements() {
            return this.brokerSettlements.values.total_income
                ? this.brokerSettlements.values.total_income.reduce(function (a, b) {
                    return a + Number(b);
                }, 0)
                : 0;
        },
        totalBrokers() {
            return this.brokerSettlements.labelsIncome ? Object.values(this.brokerSettlements.labelsIncome).length : 0;
        },
        brokers() {
            return this.brokerSettlements || this.brokerSettlements
        }
    },
    destroyed() {
        Echo.leave(`dashboard.${this.tenantId}.${this.dashboard}`)
    },
    methods: {
        graphBGColorChange() {
            return this.appearance == "white" ? "#fff" : "#000"
        },
        graphColorChange() {
            return this.appearance == 'black' ? "#000" : "#fff"
        },
        commarize(val, min) {
            min = min || 1e3;
            // Alter numbers larger than 1k
            if (val >= min) {
                var units = ["k", "M", "B", "T"];

                var order = Math.floor(Math.log(val) / Math.log(1000));

                var unitname = units[(order - 1)];
                var num = Math.floor(val / 1000 ** order);

                if (String(num).length == 1) {
                    num = Number(val / 1000 ** order).toFixed(1);
                    if (num == Number(num)) {
                        num = Number(num);
                    }
                }
                return num + unitname;
            }
            return val.toLocaleString();
        },
        adjust(color, amount) {
            return '#' + color.replace(/^#/, '').replace(/../g, color => ('0' + Math.min(255, Math.max(0, parseInt(color, 16) + amount)).toString(16)).substr(-2));
        }
    }
}
</script>

<style scoped>
.move {
    cursor: move;
}
</style>
